import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Cloud, Upload, HardDrive, LogOut, Trash2, Download, Grid, List, Sparkles, Smartphone, ArrowUpCircle, FileText, Image as ImageIcon, Film, Search, Copy, Users, Wallet } from "lucide-react";
import { api, API, formatBytes as _fmtBytesRaw } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const PLAN_META = {
  storage_516gb_lifetime: { label: "516 GB" },
  storage_1tb_lifetime:  { label: "1 TB" },
  storage_2tb_lifetime:  { label: "2 TB" },
  storage_5tb_lifetime:  { label: "5 TB" },
};

function iconFor(ct) {
  if (!ct) return FileText;
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct.startsWith("video/")) return Film;
  return FileText;
}

function Thumbnail({ file }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const Icon = iconFor(file.content_type);
  const isMedia = file.content_type?.startsWith("image/") || file.content_type?.startsWith("video/");

  useEffect(() => {
    if (!isMedia || failed) return;
    let url;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/files/${file.id}/thumbnail`, { responseType: "blob" });
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setSrc(url);
      } catch {
        setFailed(true);
      }
    })();
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [file.id, isMedia, failed]);

  if (isMedia && src && file.content_type.startsWith("image/")) {
    return <img src={src} alt={file.original_filename} className="h-24 w-full rounded-lg object-cover" />;
  }
  if (isMedia && src && file.content_type.startsWith("video/")) {
    return <video src={src} className="h-24 w-full rounded-lg object-cover" muted />;
  }
  return (
    <div className="grid h-24 place-items-center rounded-lg bg-slate-950/60">
      <Icon className="h-8 w-8 text-cyan-300" />
    </div>
  );
}

export default function Dashboard() {
  const { user, logout, refresh } = useAuth();
  const { t, formatPrice, formatBytes, locale, currency } = useT();
  const nav = useNavigate();
  const [files, setFiles] = useState([]);
  const [used, setUsed] = useState(0);
  const [view, setView] = useState("grid");
  const [devices, setDevices] = useState([]);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [search, setSearch] = useState("");
  const [uploading, setUploading] = useState(false);
  const [affiliate, setAffiliate] = useState(null);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [payoutForm, setPayoutForm] = useState({ iban: "", holder_name: "", bic: "" });
  const [payoutBusy, setPayoutBusy] = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    if (!user) nav("/");
  }, [user, nav]);

  const load = useCallback(async () => {
    try {
      const [{ data: f }, { data: d }, { data: pl }, { data: aff }] = await Promise.all([
        api.get("/files"),
        api.get("/devices"),
        api.get("/plans"),
        api.get("/affiliate/stats"),
      ]);
      setFiles(f.files || []);
      setUsed(f.used_bytes || 0);
      setDevices(d.devices || []);
      setPlans(pl.plans || []);
      setAffiliate(aff);
    } catch (err) {
      if (err?.response?.status === 401) {
        await logout();
        nav("/");
      }
    }
  }, [logout, nav]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const quota = user?.plan?.quota_bytes || 0;
  const pct = quota ? Math.min(100, (used / quota) * 100) : 0;

  const doUpload = async (fileList) => {
    if (!user?.plan) {
      toast.error("Nessun piano attivo. Riscatta un codice.");
      return;
    }
    setUploading(true);
    for (const f of Array.from(fileList)) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`Caricato: ${f.name}`);
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Errore su ${f.name}`);
      }
    }
    setUploading(false);
    load();
    refresh();
  };

  const onDrop = (e) => { e.preventDefault(); doUpload(e.dataTransfer.files); };
  const onDrag = (e) => e.preventDefault();

  const del = async (id) => {
    try {
      await api.delete(`/files/${id}`);
      toast.success("File eliminato");
      load();
    } catch { toast.error("Errore eliminazione"); }
  };

  const download = async (f) => {
    try {
      const res = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Impossibile scaricare"); }
  };

  const buyUpgrade = async (lookup_key) => {
    try {
      const suffixMap = { usd: "_usd", gbp: "_gbp", chf: "_chf", cad: "_cad" };
      const finalKey = currency !== "eur" ? `${lookup_key}${suffixMap[currency] || ""}` : lookup_key;
      const { data } = await api.post("/payments/checkout", {
        lookup_key: finalKey,
        email: user.email,
        origin_url: window.location.origin,
        locale,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const filtered = files.filter((f) =>
    (f.original_filename || "").toLowerCase().includes(search.toLowerCase())
  );

  const planLabel = user?.plan?.label || "Nessun piano";

  return (
    <div className="min-h-screen text-slate-100" data-testid="patient-dashboard">
      <header className="sticky top-0 z-30 border-b border-slate-800/80 bg-slate-950/90 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <button onClick={() => nav("/")} className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600">
              <Cloud className="h-5 w-5 text-slate-950" strokeWidth={2.5} />
            </div>
            <span className="text-lg font-bold"><span className="text-gradient">Nexus</span>Cloud</span>
          </button>
          <div className="flex items-center gap-2">
            <Button
              data-testid="dashboard-upgrade-plan-button"
              onClick={() => setUpgradeOpen(true)}
              className="bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-950 hover:opacity-90"
            >
              <ArrowUpCircle className="mr-2 h-4 w-4" /> {t("db_upgrade")}
            </Button>
            <Button
              variant="outline"
              data-testid="dashboard-redeem-btn"
              onClick={() => nav("/redeem")}
              className="border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800"
            >
              <Sparkles className="mr-2 h-4 w-4" /> {t("db_redeem")}
            </Button>
            <Button
              variant="outline"
              data-testid="dashboard-logout-btn"
              onClick={async () => { await logout(); nav("/"); }}
              className="border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800"
            >
              <LogOut className="mr-2 h-4 w-4" /> {t("db_logout")}
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        {/* Storage progress */}
        <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400">
                <HardDrive className="h-3.5 w-3.5" /> {t("db_storage_active")} {planLabel}
              </div>
              <div className="mt-2 text-2xl font-bold">
                <span className="text-cyan-300">{formatBytes(used)}</span>
                <span className="text-slate-500"> / {quota ? formatBytes(quota) : "N/D"}</span>
              </div>
              <div className="text-xs text-slate-500">{quota ? `${pct.toFixed(1)}% ${t("db_pct_used")}` : t("db_no_plan")}</div>
            </div>
            {!user?.plan && (
              <Button
                onClick={() => nav("/redeem")}
                className="bg-cyan-400 text-slate-950 hover:bg-cyan-300"
                data-testid="no-plan-redeem-btn"
              >
                <Sparkles className="mr-2 h-4 w-4" /> {t("db_no_plan_btn")}
              </Button>
            )}
          </div>
          <Progress value={pct} data-testid="dashboard-storage-progress" className="mt-4 h-2 bg-slate-800" />
        </section>

        {/* Upload zone */}
        {user?.plan && (
          <section
            onDrop={onDrop}
            onDragOver={onDrag}
            data-testid="upload-dropzone"
            className="mt-6 rounded-2xl border-2 border-dashed border-slate-700 bg-slate-900/30 p-10 text-center transition-colors hover:border-cyan-500/60"
          >
            <input
              ref={inputRef}
              type="file"
              multiple
              hidden
              data-testid="upload-file-input"
              onChange={(e) => doUpload(e.target.files)}
            />
            <Upload className="mx-auto h-10 w-10 text-cyan-300" />
            <h3 className="mt-4 text-lg font-semibold">{t("db_drop_title")}</h3>
            <p className="text-sm text-slate-400">{t("db_drop_sub_1")} {formatBytes(quota - used)} {t("db_drop_sub_2")}</p>
            <Button
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid="upload-choose-btn"
              className="mt-4 bg-cyan-400 text-slate-950 hover:bg-cyan-300"
            >
              {uploading ? t("db_uploading") : t("db_choose")}
            </Button>
          </section>
        )}

        {/* Files toolbar */}
        <section className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
              <Input
                data-testid="file-search-input"
                placeholder={t("db_search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-64 border-slate-800 bg-slate-900 pl-8 text-slate-100"
              />
            </div>
          </div>
          <div className="flex items-center gap-1 rounded-lg border border-slate-800 bg-slate-900 p-1">
            <button
              data-testid="file-view-toggle-grid"
              onClick={() => setView("grid")}
              className={`grid h-8 w-8 place-items-center rounded ${view === "grid" ? "bg-cyan-400 text-slate-950" : "text-slate-400"}`}
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              data-testid="file-view-toggle-list"
              onClick={() => setView("list")}
              className={`grid h-8 w-8 place-items-center rounded ${view === "list" ? "bg-cyan-400 text-slate-950" : "text-slate-400"}`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </section>

        {/* Files */}
        <section data-testid="file-grid-container" className="mt-4">
          {filtered.length === 0 ? (
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-10 text-center text-slate-400">
              {t("db_no_files")}
            </div>
          ) : view === "grid" ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {filtered.map((f) => {
                return (
                  <div
                    key={f.id}
                    data-testid={`file-card-${f.id}`}
                    className="card-hover rounded-xl border border-slate-800 bg-slate-900/50 p-4"
                  >
                    <Thumbnail file={f} />
                    <div className="mt-3 truncate text-sm text-slate-200">{f.original_filename}</div>
                    <div className="text-xs text-slate-500">{formatBytes(f.size)}</div>
                    <div className="mt-3 flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => download(f)}
                        className="flex-1 border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800"
                      >
                        <Download className="mr-1 h-3.5 w-3.5" /> {t("db_download")}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => del(f.id)}
                        className="border-slate-700 bg-transparent text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900/50">
              {filtered.map((f, i) => {
                const Icon = iconFor(f.content_type);
                return (
                  <div
                    key={f.id}
                    className={`flex items-center gap-4 px-4 py-3 ${i > 0 ? "border-t border-slate-800" : ""}`}
                  >
                    <Icon className="h-5 w-5 text-cyan-300" />
                    <div className="flex-1 truncate text-sm">{f.original_filename}</div>
                    <div className="w-24 text-xs text-slate-500">{formatBytes(f.size)}</div>
                    <Button size="sm" variant="outline" onClick={() => download(f)} className="border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800">
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => del(f.id)} className="border-slate-700 bg-transparent text-red-300 hover:bg-red-500/10">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Devices */}
        <section id="devices" data-testid="devices-manage-section" className="mt-10 rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">{t("db_devices_title")}</h3>
              <p className="text-xs text-slate-400">{t("db_devices_sub_1")}{" "}
                <a href="mailto:st.support25@gmail.com" className="text-cyan-300 underline">st.support25@gmail.com</a>.
              </p>
            </div>
            <span className="rounded-full border border-slate-700 bg-slate-950 px-3 py-1 text-xs text-cyan-300">
              {devices.length}/2 {t("db_devices_active")}
            </span>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {devices.map((d, i) => (
              <div
                key={d.session_id}
                data-testid={`device-item-${i + 1}`}
                className="flex items-center gap-3 rounded-lg border border-slate-800 bg-slate-950/50 p-3"
              >
                <Smartphone className="h-4 w-4 text-cyan-300" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-slate-200">{d.device_name || "Dispositivo"}</div>
                  <div className="truncate text-xs text-slate-500">{d.user_agent}</div>
                </div>
              </div>
            ))}
            {devices.length === 0 && (
              <div className="text-sm text-slate-400">{t("db_devices_none")}</div>
            )}
          </div>
          <p className="mt-3 text-xs text-slate-500">
            {t("db_devices_hint")}
          </p>
        </section>
        {/* Affiliate */}
        {affiliate && (
          <section data-testid="affiliate-section" className="mt-10 rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900/70 via-slate-900/50 to-cyan-500/5 p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-500">
                  <Users className="h-5 w-5 text-slate-950" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-emerald-300">
                    {locale === "en" ? "Affiliate program" : "Programma affiliazione"}
                  </div>
                  <div className="text-lg font-bold">
                    {locale === "en"
                      ? `Earn ${affiliate.rate_percent}% on every friend you refer`
                      : `Guadagni il ${affiliate.rate_percent}% da ogni amico che porti`}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2">
                <Wallet className="h-4 w-4 text-emerald-300" />
                <div>
                  <div className="text-[10px] uppercase text-emerald-300">{locale === "en" ? "Earnings" : "Guadagni"}</div>
                  <div data-testid="affiliate-earnings" className="text-lg font-bold text-emerald-200">
                    €{((affiliate.earnings_cents || 0) / 100).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-3">
              <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">
                  {locale === "en" ? "Signups" : "Iscrizioni"}
                </div>
                <div data-testid="affiliate-signups" className="text-2xl font-bold text-slate-100">{affiliate.signup_count}</div>
              </div>
              <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">
                  {locale === "en" ? "Paying friends" : "Amici paganti"}
                </div>
                <div data-testid="affiliate-paying" className="text-2xl font-bold text-slate-100">{affiliate.paying_count}</div>
              </div>
              <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-400">
                  {locale === "en" ? "Your code" : "Il tuo codice"}
                </div>
                <div className="mono text-2xl font-bold text-cyan-300">{affiliate.referral_code}</div>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2 rounded-xl border border-slate-800 bg-slate-950/80 p-3">
              <code
                data-testid="affiliate-link"
                className="mono flex-1 truncate text-xs text-slate-300"
              >
                {window.location.origin}/?ref={affiliate.referral_code}
              </code>
              <Button
                size="sm"
                data-testid="affiliate-copy-btn"
                onClick={() => {
                  const link = `${window.location.origin}/?ref=${affiliate.referral_code}`;
                  navigator.clipboard.writeText(link).then(() => {
                    toast.success(locale === "en" ? "Link copied!" : "Link copiato!");
                  });
                }}
                className="bg-cyan-400 text-slate-950 hover:bg-cyan-300"
              >
                <Copy className="mr-1 h-3.5 w-3.5" /> {locale === "en" ? "Copy" : "Copia"}
              </Button>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3">
              <div className="text-xs text-slate-300">
                {affiliate.pending_payout ? (
                  <span data-testid="payout-pending-msg">
                    {locale === "en"
                      ? `Payout request of €${(affiliate.pending_payout.amount_cents/100).toFixed(2)} in progress`
                      : `Richiesta di payout €${(affiliate.pending_payout.amount_cents/100).toFixed(2)} in corso`}
                  </span>
                ) : affiliate.earnings_cents >= affiliate.min_payout_cents ? (
                  <span>{locale === "en" ? "Ready to withdraw" : "Pronto per il ritiro"}</span>
                ) : (
                  <span>
                    {locale === "en"
                      ? `Minimum payout: €${(affiliate.min_payout_cents/100).toFixed(0)}`
                      : `Minimo payout: €${(affiliate.min_payout_cents/100).toFixed(0)}`}
                  </span>
                )}
              </div>
              <Button
                size="sm"
                data-testid="request-payout-btn"
                disabled={!!affiliate.pending_payout || affiliate.earnings_cents < affiliate.min_payout_cents}
                onClick={() => setPayoutOpen(true)}
                className="bg-emerald-400 text-slate-950 hover:bg-emerald-300 disabled:bg-slate-800 disabled:text-slate-500"
              >
                <Wallet className="mr-1 h-3.5 w-3.5" />
                {locale === "en" ? "Request payout" : "Richiedi payout"}
              </Button>
            </div>
            <p className="mt-2 text-[11px] text-slate-500">
              {locale === "en"
                ? `Share your link. When someone signs up with it and buys any plan, you earn ${affiliate.rate_percent}% of the sale automatically. Payout on request via support.`
                : `Condividi il tuo link. Quando qualcuno si iscrive con esso e compra un piano, guadagni il ${affiliate.rate_percent}% della vendita automaticamente. Payout su richiesta via supporto.`}
            </p>
          </section>
        )}
      </main>

      {/* Upgrade modal */}
      <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
        <DialogContent data-testid="upgrade-modal-container" className="max-w-2xl border-slate-800 bg-slate-950 text-slate-100">
          <DialogHeader>
            <DialogTitle>{t("db_upgrade_title")}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {t("db_upgrade_desc")}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {plans.map((p) => {
              const isCurrent = user?.plan_lookup_key === p.lookup_key;
              const isSmaller = user?.plan && p.quota_bytes <= user.plan.quota_bytes;
              const meta = PLAN_META[p.lookup_key] || { label: p.label };
              return (
                <div key={p.lookup_key} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-lg font-semibold">{meta.label}</div>
                      <div className="text-xs text-slate-400">{t("db_lifetime_once")}</div>
                    </div>
                    <div className="text-xl font-bold text-cyan-300">{formatPrice(p.prices)}</div>
                  </div>
                  <Button
                    onClick={() => buyUpgrade(p.lookup_key)}
                    disabled={isCurrent || isSmaller}
                    data-testid={`upgrade-btn-${p.lookup_key}`}
                    className="mt-3 w-full bg-cyan-400 text-slate-950 hover:bg-cyan-300"
                  >
                    {isCurrent ? t("db_current") : isSmaller ? t("db_smaller") : `${t("db_upgrade_to")} ${meta.label}`}
                  </Button>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Payout modal */}
      <Dialog open={payoutOpen} onOpenChange={setPayoutOpen}>
        <DialogContent data-testid="payout-modal" className="max-w-md border-slate-800 bg-slate-950 text-slate-100">
          <DialogHeader>
            <DialogTitle>
              {locale === "en" ? "Request affiliate payout" : "Richiedi payout affiliazione"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {locale === "en"
                ? `Withdraw €${((affiliate?.earnings_cents || 0)/100).toFixed(2)} to your bank account (2-5 business days).`
                : `Ritira €${((affiliate?.earnings_cents || 0)/100).toFixed(2)} sul tuo conto bancario (2-5 giorni lavorativi).`}
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setPayoutBusy(true);
              try {
                await api.post("/affiliate/payout", payoutForm);
                toast.success(locale === "en" ? "Payout requested!" : "Payout richiesto!");
                setPayoutOpen(false);
                setPayoutForm({ iban: "", holder_name: "", bic: "" });
                load();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Errore");
              } finally {
                setPayoutBusy(false);
              }
            }}
            className="space-y-3"
          >
            <div>
              <label className="text-xs text-slate-400">{locale === "en" ? "Account holder name" : "Intestatario conto"}</label>
              <input
                required
                data-testid="payout-holder-input"
                value={payoutForm.holder_name}
                onChange={(e) => setPayoutForm({ ...payoutForm, holder_name: e.target.value })}
                className="mt-1 w-full rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400">IBAN</label>
              <input
                required
                data-testid="payout-iban-input"
                placeholder="IT60 X054 2811 1010 0000 0123 456"
                value={payoutForm.iban}
                onChange={(e) => setPayoutForm({ ...payoutForm, iban: e.target.value.toUpperCase() })}
                className="mono mt-1 w-full rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400">{locale === "en" ? "BIC/SWIFT (optional)" : "BIC/SWIFT (opzionale)"}</label>
              <input
                data-testid="payout-bic-input"
                value={payoutForm.bic}
                onChange={(e) => setPayoutForm({ ...payoutForm, bic: e.target.value.toUpperCase() })}
                className="mono mt-1 w-full rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              />
            </div>
            <Button
              type="submit"
              disabled={payoutBusy}
              data-testid="payout-submit-btn"
              className="w-full bg-emerald-400 text-slate-950 hover:bg-emerald-300"
            >
              {payoutBusy ? (locale === "en" ? "Sending..." : "Invio...") : (locale === "en" ? "Confirm payout request" : "Conferma richiesta payout")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
